import React, { useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { toast } from 'sonner';
import { PhoneForwarded, Search, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { isTouchViewport } from '@/hooks/use-mobile';
import type { RootState } from '@/reducers';
import type { User } from '@/types';
import { keepPageDialogsOpen, useCompactCallUi } from './useCompactCallUi';

interface ForwardCallPanelProps {
	isOpen: boolean;
	onClose: () => void;
	callSid: string | null;
	onForwardInitiated: (target: { _id: string; name: string }) => void;
}

const getInitials = (name: string): string => {
	const parts = name.trim().split(/\s+/);
	if (parts.length >= 2) {
		return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
	}
	return name.slice(0, 2).toUpperCase();
};

const ForwardCallPanel: React.FC<ForwardCallPanelProps> = ({
	isOpen,
	onClose,
	callSid,
	onForwardInitiated,
}) => {
	const [search, setSearch] = useState('');
	const [submittingFor, setSubmittingFor] = useState<string | null>(null);
	const isCompactCallUi = useCompactCallUi();

	const allUsers = useSelector((state: RootState) =>
		(state as any).users.filter((u: any) => u.active),
	);
	const auth = useSelector((state: RootState) => state.auth);
	const ongoingCalls = useSelector(
		(state: RootState) => state.voice.ongoingCalls || [],
	);

	const currentUserId =
		(auth as any)?.user?._id?.toString?.() || (auth as any)?.user?._id;

	const busyUserIds = useMemo(() => {
		const ids = new Set<string>();
		(ongoingCalls as any[]).forEach((call) => {
			if (
				call?._user &&
				['initiated', 'ringing', 'in-progress', 'answered'].includes(
					call.callStatus,
				)
			) {
				const id =
					typeof call._user === 'object' ? call._user?._id : call._user;
				if (id) ids.add(id.toString());
			}
		});
		return ids;
	}, [ongoingCalls]);

	const filteredUsers = useMemo(() => {
		const q = search.trim().toLowerCase();
		return (allUsers as User[])
			.filter((u: any) => u._id?.toString() !== currentUserId)
			.filter((u: any) => {
				if (!q) return true;
				const haystack = `${u.name || ''} ${u.username || ''}`.toLowerCase();
				return haystack.includes(q);
			});
	}, [allUsers, search, currentUserId]);

	useEffect(() => {
		if (!isOpen) {
			setSearch('');
			setSubmittingFor(null);
			return;
		}
		const handleKey = (e: KeyboardEvent) => {
			if (e.key === 'Escape') onClose();
		};
		window.addEventListener('keydown', handleKey);
		return () => window.removeEventListener('keydown', handleKey);
	}, [isOpen, onClose]);

	const handleForward = async (user: any) => {
		if (busyUserIds.has(user._id?.toString())) {
			toast.error(`${user.name || user.username} is on another call`);
			return;
		}

		setSubmittingFor(user._id);
		try {
			await axios.post('/api/voice/forward-call', {
				_toUser: user._id,
			});
			onForwardInitiated({
				_id: user._id,
				name: user.name || user.username || 'teammate',
			});
			onClose();
		} catch (err: any) {
			const message =
				err?.response?.data?.error ||
				'Failed to forward call. Please try again.';
			toast.error(message);
		} finally {
			setSubmittingFor(null);
		}
	};

	if (!isOpen) return null;

	const modal = (
		<div
			className={cn(
				'fixed inset-0 z-[1100] flex items-center justify-center p-4',
				// Clear the on-screen keyboard (0px on desktop). Phones get a
				// bottom sheet instead of the centred card.
				'pb-[calc(1rem+var(--keyboard-inset,0px))] max-md:items-end max-md:p-0 max-md:pb-[var(--keyboard-inset,0px)]',
				// Over the phone call screen: stay tappable, and keep a dialog
				// open on the page from closing, even while it is modal.
				isCompactCallUi && 'pointer-events-auto',
			)}
			onClick={onClose}
			onMouseDown={(e) => e.stopPropagation()}
			onPointerDown={isCompactCallUi ? keepPageDialogsOpen : undefined}
		>
			{/* Backdrop */}
			<div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />

			{/* Modal panel */}
			<div
				role="dialog"
				aria-modal="true"
				aria-label="Forward call"
				className={cn(
					'relative w-full max-w-sm bg-gray-950 border border-gray-800 rounded-2xl shadow-2xl shadow-black/50 flex flex-col max-h-[80vh]',
					'max-xl:max-h-[min(80vh,calc(var(--app-height)-2rem))] max-xl:overflow-y-auto max-md:max-w-none max-md:max-h-[calc(var(--app-height)-var(--safe-top)-1.5rem)] max-md:rounded-b-none max-md:border-x-0 max-md:border-b-0 max-md:pl-safe max-md:pr-safe max-md:animate-in max-md:slide-in-from-bottom max-md:duration-300',
				)}
				onClick={(e) => e.stopPropagation()}
				onMouseDown={(e) => e.stopPropagation()}
			>
				<div className="flex items-center justify-between p-4 border-b border-gray-800">
					<div className="flex items-center gap-2">
						<PhoneForwarded className="h-4 w-4 text-blue-400" />
						<h3 className="text-sm font-semibold text-white">
							Forward Call
						</h3>
					</div>
					<button
						type="button"
						onClick={onClose}
						className="text-gray-400 hover:text-white p-1 rounded-md hover:bg-gray-900 max-md:-my-2 max-md:-mr-2 max-md:flex max-md:size-11 max-md:items-center max-md:justify-center max-md:rounded-full"
						aria-label="Close forward panel"
					>
						<X className="h-4 w-4 max-md:h-5 max-md:w-5" />
					</button>
				</div>

				<div className="p-4 pb-2">
					<div className="relative">
						<Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-500" />
						<input
							type="text"
							value={search}
							onChange={(e) => setSearch(e.target.value)}
							placeholder="Search teammates..."
							aria-label="Search teammates"
							className="w-full bg-gray-900 border border-gray-800 rounded-lg pl-8 pr-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-blue-600 max-md:min-h-11 max-md:text-base"
							autoComplete="off"
							autoCorrect="off"
							autoCapitalize="none"
							spellCheck={false}
							enterKeyHint="search"
							// On touch screens the keyboard would cover the list before
							// anyone has looked at it.
							autoFocus={!isTouchViewport()}
						/>
					</div>
				</div>

				{/* Keeps the first match in view above a landscape keyboard. */}
				<div className="flex-1 overflow-y-auto px-2 pb-2 max-xl:min-h-[3.25rem]">
					{filteredUsers.length === 0 && (
						<div className="text-xs text-gray-500 text-center py-6">
							No teammates available
						</div>
					)}
					{filteredUsers.map((user: any) => {
						const userId = user._id?.toString();
						const isBusy = busyUserIds.has(userId);
						const isSubmitting = submittingFor === user._id;
						const displayName =
							user.name || user.username || 'Unknown';

						return (
							<button
								key={userId}
								type="button"
								onClick={() => handleForward(user)}
								disabled={isBusy || isSubmitting}
								className={cn(
									'w-full flex items-center gap-3 p-2 rounded-lg text-left transition-colors max-md:min-h-[3.25rem] max-md:px-3',
									'hover:bg-gray-900 active:bg-gray-800',
									(isBusy || isSubmitting) &&
										'opacity-50 cursor-not-allowed hover:bg-transparent',
								)}
							>
								<div className="flex items-center justify-center w-9 h-9 rounded-full bg-gray-800 border border-gray-700 flex-shrink-0">
									<span className="text-xs font-semibold text-white">
										{getInitials(displayName)}
									</span>
								</div>
								<div className="flex-1 min-w-0">
									<p className="text-sm text-white truncate max-md:text-base">
										{displayName}
									</p>
									{isBusy && (
										<p className="text-xs text-gray-500">
											On another call
										</p>
									)}
									{isSubmitting && (
										<p className="text-xs text-blue-400">
											Forwarding...
										</p>
									)}
								</div>
								<PhoneForwarded className="h-4 w-4 text-gray-500" />
							</button>
						);
					})}
				</div>

				{/* The header's close button stays while the keyboard is up. */}
				<div className="p-3 border-t border-gray-800 max-md:pb-[max(0.75rem,var(--safe-bottom))] kb-open:hidden">
					<button
						type="button"
						onClick={onClose}
						className="w-full text-sm text-gray-400 hover:text-white py-2 rounded-lg hover:bg-gray-900/60 max-md:min-h-11 max-md:text-base"
					>
						Cancel
					</button>
				</div>
			</div>
		</div>
	);

	return ReactDOM.createPortal(modal, document.body);
};

export default ForwardCallPanel;
